/**
 * Account functions are used for reaching the endpoints for accessing account data.
 * @module Account
 * @since 0.0.4
 */
import GlobalFunctions from '../global'

module.exports = {
	/**
	 * Call to unoapp API that returns an array of account details that the current user can log into.
	 * @function
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	getUserAccounts () {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/account/list',
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns an array of account details that belong to the specified business.
	 * @function
	 * @param {integer} businessId - The id of the business.
	 * @param {integer} locationId - The id of the location.
	 * @param {string} accountType - The account type that we are pulling users for.
	 * @param {integer} pageNumber - The current page that we are retrieving results for.
	 * @param {integer} recordsPerPage - The number of records to return per page.
	 * @param {object} searchCriteria - Object with all criteria to be searched by.
	 * @param {object} sortCriteria - The field and order to be sorted by.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	getUsers (businessId, locationId, accountType, pageNumber, recordsPerPage, searchCriteria, sortCriteria) {
		let httpParams = {business_id: businessId, location_id: locationId, account_type: accountType, page_number: pageNumber, records_per_page: recordsPerPage}
		if (sortCriteria) {
			for (let sortKey in sortCriteria) {
				httpParams[sortKey] = sortCriteria[sortKey]
			}
		}

		if (searchCriteria) {
			for (let searchKey in searchCriteria) {
				httpParams[searchKey] = searchCriteria[searchKey]
			}
		}
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/account/search',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns data about the selected account for the JWT token.
	 * @function
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	getCurrentAccountDetails () {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/account/self',
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns data about the account id passed in.
	 * @function
	 * @param {integer} accountId - The id of the account we want details about.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 */
	getAccountDetails (accountId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/account/' + accountId,
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that deletes the specified account.
	 * @function
	 * @param {object} accountId - The id of the account to be terminated.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.5
	 * @todo Remove timeout
	 */
	deleteUser (accountId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'DELETE',
				dataType: 'json',
				url: '/account/' + accountId,
				data: {},
				success (response) {
					setTimeout(() => {
						resolve(response)
					}, 1000)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API to get the list of locations for a business.
	 * @function
	 * @param {integer} businessId - The business_id of the business that the locations should be returned for.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @memberof Account
	 * @version 0.0.5
	 */
	getBusinessLocations (businessId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/account/locations_list',
				data: {business_id: businessId},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API to get the list of locations for a business based on the search field.
	 * @function
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @memberof Account
	 * @version 0.0.5
	 */
	getLocationSearchResults () {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/account/locations_search',
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API to get the list of businesses based on the search field.
	 * @function
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @memberof Account
	 * @version 0.0.5
	 */
	getBusinessSearchResults () {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/account/business_search',
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	}
}



// WEBPACK FOOTER //
// ./src/controllers/Account.js